import jwtDecode from "jwt-decode";
import { useEffect, useState, useMemo } from "react";

// Role-based component mapping
const ROLE_COMPONENT_MAP = {
  "Auction Super Admin": () =>
    import("../../components/dashboard/ASADashboard"),
  "Auction Representative": () =>
    import("../../components/meratractor/ARDashboard"),
  "Marketing Coordinator": () =>
    import("../../components/meratractor/MACODashboard"),
  "Auction Back Office": () =>
    import("../../components/auction/AuctionBackOffice/AuctionBackOfficeDashboard"),
  "Super Admin": () =>
    import("../../features/auction/dashboard/SADeveloperAuctionDashboard"),
  Developer: () =>
    import("../../features/auction/dashboard/SADeveloperAuctionDashboard"),
  "Tech Head": () =>
    import("../../features/auction/dashboard/SADeveloperAuctionDashboard"),
  "Auction CEO": () =>
    import("../../features/auction/dashboard/AuctionCEODashboard"),
};

function Dashboard() {
  const [Component, setComponent] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Memoize user to avoid unnecessary re-renders
  const user = useMemo(() => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) return null;
      return jwtDecode(refreshToken);
    } catch (err) {
      console.error("Error decoding token:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    const loadComponent = async () => {
      if (!user?.role) {
        setIsLoading(false);
        return;
      }

      const componentLoader = ROLE_COMPONENT_MAP[user.role];

      if (!componentLoader) {
        setError(`Unauthorized role: ${user.role}`);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const module = await componentLoader();
        setComponent(() => module.default);
        setError(null);
      } catch (err) {
        console.error("Error loading component:", err);
        setError("Failed to load dashboard component");
      } finally {
        setIsLoading(false);
      }
    };

    loadComponent();
  }, [user]);

  // Handle no user
  if (!user) {
    return (
      <div className="alert alert-warning">No user found. Please login.</div>
    );
  }

  // Handle error state
  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  // Handle loading state
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Render component
  if (Component) {
    return <Component />;
  }

  return null;
}

export default Dashboard;
